import React, { useState, useRef } from 'react';
import Icon from './Icon';
import '../styles/ImageUpload.css';
import { uploadToS3, validateFile as validateFileUtil } from '../utils/s3Upload';

const ImageUpload = ({
  value = '',
  onChange,
  onClear,
  label = 'Upload Image',
  required = false,
  maxSizeMB = 5,
  fileType = 'organization_icon',
  metadata = {}
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
  const isIconUpload = fileType === 'organization_icon' || fileType === 'brand_icon';

  const validateFile = (file) => {
    const result = validateFileUtil(file, maxSizeMB, ALLOWED_TYPES);
    if (!result.valid) {
      return result.error;
    }
    return null;
  };

  const handleFile = async (file) => {
    setError('');
    setUploadProgress(0);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const result = await uploadToS3(
        file,
        fileType,
        metadata,
        (percent) => {
          setUploadProgress(percent);
        }
      );

      if (result.success) {
        const sizeKB = (file.size / 1024).toFixed(2);
        const fileTypeLabel = file.type.split('/')[1].toUpperCase();

        setFileInfo({
          name: file.name,
          size: sizeKB,
          type: fileTypeLabel
        });

        onChange(result.file_url);
      } else {
        setError(result.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      console.error('Image upload error:', err);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setFileInfo(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClear();
  };

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">
        {label}
        {required && <span className="required-asterisk"> *</span>}
      </label>

      {!value ? (
        <div
          className={`image-upload-zone ${isDragging ? 'drag-over' : ''} ${error ? 'error' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {isLoading ? (
            <div className="upload-loading">
              <div className="loading-spinner"></div>
              <p>Uploading... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <div className="upload-icon"><Icon name="upload-cloud" size="3.5rem" /></div>
              <p className="upload-text">
                <strong>Drag and drop</strong> an image here.
              </p>
              <p className="upload-hint">
                Supports PNG, JPG, SVG, WEBP (max {maxSizeMB}MB)
              </p>
              <button
                type="button"
                className="upload-btn"
                onClick={(e) => { e.stopPropagation(); handleClick(); }}
              >
                Upload files
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="image-preview-container">
          <div className={`image-preview${isIconUpload ? ' icon-preview' : ''}`}>
            <img src={value} alt="Preview" />
            <button
              type="button"
              className="remove-image-btn"
              onClick={handleClear}
              title="Remove image"
            >
              ×
            </button>
          </div>
          {fileInfo && (
            <div className="file-info">
              <p className="file-name">{fileInfo.name}</p>
              <p className="file-details">
                {fileInfo.type} • {fileInfo.size} KB
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
